import { Map } from './Map.js';
import { Layer } from './layer/Layer.js';
import { debounceCallback } from '../utils/utils.js';
import { GeoJSONLayer } from './layer/GeoJSONLayer.js';
import { extendLeaflet } from './leaflet/extendLeaflet.js';
import { roundNumber } from '../utils/utils.js';
import { ContextMenu } from '../ui/ContextMenu.js';

import "./Map2D.css";

export { Map2D };

/**
 * 2D Map Implementation based on leaflet
 *
 * @author rhess <robin.hess@awi.de>
 * @author sjaswal <shahzeib.jaswal@awi.de>
 * @author rkoppe <roland.koppe@awi.de>
 * 
 * @memberof vef.map
 */
class Map2D extends Map {

    static leafletInitialized_ = false;

    /** 
     * @param {string/HTMLElement} target id or HTMLElement
     * @param {Object} options options object
     */
    constructor(target, options) {
        super(target, options);

        // initialize custom Leaflet extensions for the VEF
        extendLeaflet()

        this.leafletMap_ = null;
        this.activeArea_ = null;
        this.resizeObserver_ = null;
        this.contextMenu_ = null;

        this.getElement().classList.add("map-2d");

        this.initMap_();
        this.initDrawEvents_();
        if (this.options.ctrlToZoom) this.initCtrlToZoom_();
        if (this.options.responsiveMaxBounds) setTimeout(() => this.calculateResponsiveMaxZoom_(), 0);

        this.setActiveArea("default-active-area", false);
        this.initContextMenu_();
    }

    calculateResponsiveMaxZoom_() {
        this.leafletMap_.setMinZoom(null);
        const bbox = Map.getFitToScreenBounds(this.options.crs);
        const zoom = this.leafletMap_.getBoundsZoom([[bbox.min.y, bbox.min.x], [bbox.max.y, bbox.max.x]]);
        this.leafletMap_.setMinZoom(zoom);

        if (["EPSG:3857", "EPSG:4326"].includes(this.options.crs)) {
            this.leafletMap_.setMaxBounds([[bbox.min.y, -Infinity], [bbox.max.y, Infinity]]);
        } else {
            this.leafletMap_.setMaxBounds(null);
        }
    }

    /**
     * @private
     */
    initResponsiveMaxBounds_() {
        this.options.maxBounds = null;
        this.leafletMap_.setMaxBounds(null);

        // update maxZoom when the map gets resized
        this.resizeObserver_ = new ResizeObserver(debounceCallback(() => this.calculateResponsiveMaxZoom_(), 100));
        this.resizeObserver_.observe(this.leafletMap_.getContainer());

        setTimeout(() => this.calculateResponsiveMaxZoom_(), 0);
    }

    initContextMenu_() {
        let clickedCoordinates = "";
        
        const htmlElement = this.leafletMap_.getContainer();
        const getLatLng = (node, x, y) => {
            const latLng = this.leafletMap_.containerPointToLatLng([x, y]).wrap();
            const lat = roundNumber(latLng.lat, 3, 3);
            const lng = roundNumber(latLng.lng, 3, 3);
            clickedCoordinates = lat + ", " + lng;
            return clickedCoordinates;
        };

        this.contextMenu_ = new ContextMenu();
        this.contextMenu_.registerElement(htmlElement, [
            {
                icon: "fas fa-map-marked-alt",
                text: getLatLng,
                callback: () => {
                    if (navigator.clipboard) {
                        navigator.clipboard.writeText(clickedCoordinates);
                        console.warn("copied coordinates:" + clickedCoordinates);
                    } else {
                        console.warn("navigator.clipboard not available");
                    }
                }
            }
        ]);
    }

    /**
     * @private
     */
    initCtrlToZoom_() {
        const element = this.getElement();
        let resetScrollTimeout = null;

        this.leafletMap_.scrollWheelZoom.disable();

        const eventCallback = (event) => {
            event.stopPropagation();
            if (event.ctrlKey == true) {
                event.preventDefault();
                this.leafletMap_.scrollWheelZoom.enable();
                element.classList.remove('ctrl-To-zoom');
            } else {
                this.leafletMap_.scrollWheelZoom.disable();
                element.classList.add('ctrl-To-zoom');
            }
            clearTimeout(resetScrollTimeout);
            resetScrollTimeout = setTimeout(() => {
                this.leafletMap_.scrollWheelZoom.disable();
                element.classList.remove('ctrl-To-zoom');
            }, 2000);
        }

        element.addEventListener('mousewheel', eventCallback, false);
        element.addEventListener('DOMMouseScroll', eventCallback, false);
    }

    /**
     * initialize div containers
     */
    initMap_() {
        this.leafletMap_ = new L.map(this.mapContainer_, {
            minZoom: this.options.minZoom,
            maxZoom: this.options.maxZoom,
            zoomSnap: 0,
            zoomControl: false,
            doubleClickZoom: this.options.doubleClickZoom,
            crs: L.CRS[this.options.crs.replace(":", "")],
            // disable feature for safari bug in leaflet 1.7.1
            tap: false
        });

        // build wrapper for events
        this.leafletMap_.on("moveend", () => this.fire("move_end", this));
        this.leafletMap_.on("zoomend", () => this.fire("zoom_end", this));
        this.leafletMap_.on('click', e => {
            this.fire("click", {
                lat: e.latlng.lat,
                lng: e.latlng.lng
            })
        });
        this.leafletMap_.on("mousemove", (e) => {
            const latLng = e.latlng.wrap();
            this.fire("mousemove", { lat: latLng.lat, lng: latLng.lng })
        });

        // close popups when certain events happen
        if (this.options.autoClosePopups) {
            this.leafletMap_.on('zoomend', () => this.leafletMap_.closePopup());
            this.on("add_layer", () => this.leafletMap_.closePopup());
            this.on("remove_layer", () => this.leafletMap_.closePopup());
            this.on("projection_change", () => this.leafletMap_.closePopup());
        }

        this.setView(this.options.initialCenter, this.options.initialZoom);
        this.setMaxBounds(this.options.maxBounds);
    }

    initDrawEvents_() {
        if (!L.Draw) return;

        this.leafletMap_.on(L.Draw.Event.DRAWSTART, () => {
            this.editing = true;
        });

        this.leafletMap_.on(L.Draw.Event.DRAWSTOP, () => {
            this.editing = false;
        });

        this.leafletMap_.on(L.Draw.Event.CREATED, e => {
            this.stopDrawing();

            const leafletLayer = e.layer;

            leafletLayer.feature = leafletLayer.feature || {};
            leafletLayer.feature.type = leafletLayer.feature.type || "Feature";
            leafletLayer.feature.properties = leafletLayer.feature.properties || {};
            leafletLayer.feature.properties.Color = GeoJSONLayer.color;

            const layer = new GeoJSONLayer({
                title: e.layerType,
                geoJSON: leafletLayer.toGeoJSON()
            });

            this.fire("create_layer", layer);
        })
    }

    /**
     * reset viewport of the map.
     * necessary after resizing the div or switching the projection
     */
    resetViewport() {
        this.leafletMap_.invalidateSize(false);
        if (this.options.responsiveMaxBounds) this.initResponsiveMaxBounds_();
        if (this.activeArea_) this.setActiveArea(this.activeArea_, true);
    }

    /**
     * Deprecated. Replaced by public function. 
     * 
     * @private
     */
    resetViewport_() { this.resetViewport(); }

    /**
     * Append the map to a target element.
     * {@code target} can be an "#id" with with or without a hash in the beginning
     * or an instance of HTMLElement.
     * 
     * @param {string | HTMLElement} target id or HTMLElement
     * @param {string | HTMLElement} position (optional, default=beforeend) beforebegin, afterbegin, beforeend, afterend
     * 
     * @override
     */
    appendTo(target, position) {
        super.appendTo(target, position);
        if (this.leafletMap_) {
            // push to end of event loop to wait for the current
            // execution stack (appending element) to finish
            setTimeout(() => this.resetViewport(), 0);
        }
    }

    /**
     * Wrapper method to zoom in one step
     */
    zoomIn() {
        this.leafletMap_.zoomIn();
    }

    /**
     * Wrapper method to zoom out one step
     */
    zoomOut() {
        this.leafletMap_.zoomOut();
    }

    /**
     * Wrapper method to get the current zoom of the map
     * 
     * @returns {number} zoom level
     */
    getZoom() {
        return this.leafletMap_.getZoom();
    }

    /**
     * Wrapper method to get the min zoom of the map
     * 
     * @returns {number} min zoom level
     */
    getMinZoom() {
        return this.leafletMap_.getMinZoom();
    }

    /**
     * Wrapper method to get the max zoom of the map
     * 
     * @returns {number} max zoom level
     */
    getMaxZoom() {
        return this.leafletMap_.getMaxZoom();
    }

    /**
     * Wrapper method for showing a popup on the map
     * @param {number} lat 
     * @param {number} lng 
     * @param {string} content 
     */
    showPopup(lat, lng, content) {
        let popup = null;

        if (this.options.responsivePopup) {
            popup = new L.ResponsivePopupMod({ maxWidth: "auto", });
        } else {
            popup = L.popup({ maxWidth: "auto", autoPanPaddingTopLeft: L.point(10, 30), autoPanPaddingBottomRight: L.point(10, 10) });
        }

        popup.setLatLng({ lat: lat, lng: lng });
        popup.setContent(content);
        popup.openOn(this.leafletMap_);
        this.fire("show_popup", {
            lat: lat,
            lng: lng,
            content: content
        });
    }

    /**
     * Close the currently opened popup
     */
    closePopup() {
        this.leafletMap_.closePopup()
    }

    /**
     * @param {number[]} latLng 
     * @param {number} zoom 
     */
    setView(latLng, zoom) {
        this.leafletMap_.setView(latLng, zoom);
    }

    /**
     * @param {object} bbox { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    setMaxBounds(bbox) {
        if (bbox && (bbox.crs == "CRS:84")) {
            this.leafletMap_.setMaxBounds([[bbox.min.y, bbox.min.x], [bbox.max.y, bbox.max.x]]);
        }
    }

    /**
     * @param {object} bbox { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    fitBounds(bbox) {
        if (bbox.crs == "CRS:84") {
            this.leafletMap_.fitBounds([[bbox.min.y, bbox.min.x], [bbox.max.y, bbox.max.x]]);
        }
    }

    /**
     * Get the current bounds of the map
     * 
     * @returns {object} { min: {x, y}, max: {x, y}, crs: "CRS:84" }
     */
    getBounds() {
        const bounds = this.leafletMap_.getBounds();
        const nw = bounds.getNorthWest();
        const se = bounds.getNorthWest();

        return {
            min: {
                x: (nw.lng < se.lng) ? nw.lng : se.lng,
                y: (nw.lat < se.lat) ? nw.lat : se.lat
            },
            max: {
                x: (nw.lng > se.lng) ? nw.lng : se.lng,
                y: (nw.lat > se.lat) ? nw.lat : se.lat
            },
            crs: "CRS:84"
        };
    }

    /**
     * Generic Add Layer method. Only adds a layer if the map crs code matches
     * 
     * @param {Layer} layer
     */
    addLayer(layer) {
        if ((layer instanceof Layer) && !this.layers_.includes(layer) && layer.setProjection(this.options.crs)) {
            const leafletLayer = layer.getMapLayer("leaflet");
            if (leafletLayer) {
                layer.enable("leaflet");
                this.layers_.push(layer);
                this.leafletMap_.addLayer(leafletLayer);
                this.fire("add_layer", layer);
            }
        }
    }

    /**
     * Generic Remove Layer method
     * 
     * @param {Layer} layer
     */
    removeLayer(layer) {
        const index = this.layers_.indexOf(layer);
        if (index >= 0) {
            this.layers_.splice(index, 1);
            layer.getMapLayer("leaflet").remove();
            layer.disable();
            this.fire("remove_layer", layer);
        }
    }

    /**
     * Build a bounding box from a click location
     * for getFeatureInfo requests
     * 
     * @param {number} lat click lat
     * @param {number} lng click lng
     */
    getFeatureInfoOptions(lat, lng) {
        const container = this.leafletMap_.getContainer();
        const click = this.leafletMap_.latLngToContainerPoint({ lat: lat, lng: lng });

        const bounds = this.leafletMap_.getPixelBounds();
        let northWest = this.leafletMap_.unproject(bounds.getBottomLeft());
        let southEast = this.leafletMap_.unproject(bounds.getTopRight());

        // reproject points to current projection
        const crs = this.options.crs;
        const projectedClick = proj4("EPSG:4326", crs, { x: lng, y: lat });
        northWest = proj4("EPSG:4326", crs, { x: northWest.lng, y: northWest.lat });
        southEast = proj4("EPSG:4326", crs, { x: southEast.lng, y: southEast.lat });

        return {
            container: {
                width: container.offsetWidth,
                height: container.offsetHeight,
                x: roundNumber(click.x),
                y: roundNumber(click.y)
            },
            click: {
                x: projectedClick.x,
                y: projectedClick.y
            },
            min: {
                x: (northWest.x < southEast.x) ? northWest.x : southEast.x,
                y: (northWest.y < southEast.y) ? northWest.y : southEast.y
            },
            max: {
                x: (northWest.x >= southEast.x) ? northWest.x : southEast.x,
                y: (northWest.y >= southEast.y) ? northWest.y : southEast.y
            },
            crs: crs
        };
    }

    /**
     * sets the active area div
     * 
     * @param {string} className css class name
     * @param {boolean} centerMap automatically re-center the map (default = false)
     */
    setActiveArea(className, centerMap) {
        if (this.leafletMap_._viewport) delete this.leafletMap_._viewport;

        if (this.activeArea_) {
            this.toolOverlay_.classList.remove(this.activeArea_);
            const viewport = this.mapContainer_.querySelector("." + this.activeArea_);
            if (viewport) viewport.remove();
        }

        this.activeArea_ = className;
        this.leafletMap_.setActiveArea(className, centerMap);
        this.toolOverlay_.classList.add(className);
    }

    /**
     * Set baselayer and projection. Crs and layer need to match.
     * Either option can be ommited, if the current baselayer/crs
     * matches the changed property
     * 
     * @param {Layer} layer 
     * @param {string} crs 
     */
    setBaseLayer(layer, crs) {
        if (!crs) crs = this.options.crs;
        if (!layer) layer = this.baseLayer_;

        const code = crs.replace(":", "");
        if (!(code in L.CRS)) throw new Error("Invalid CRS:", crs);

        const oldBaseLayer = this.baseLayer_;
        const oldCrs = this.options.crs;

        // remove old baselayer
        if (this.baseLayer_) {
            this.baseLayer_.getMapLayer("leaflet").remove();
            this.baseLayer_.disable();
            this.baseLayer_ = null;
            this.options.baseLayer = null;
        }

        // apply crs to map
        this.options.crs = crs;
        this.leafletMap_.options.crs = L.CRS[code];

        // set baselayer
        if (layer && layer.availableCrs.includes(crs)) {
            this.baseLayer_ = layer;
            this.options.baseLayer = layer.title;
            if (oldBaseLayer != layer) {
                this.fire("baselayer_change", { crs: crs, baseLayer: layer });
            }
        }

        // apply changed projection
        if (crs != oldCrs) {
            this.leafletMap_.invalidateSize();

            // reload the layers. only layers with matched crs are re-added
            for (let i = this.layers_.length - 1; i >= 0; --i) {
                const layer = this.layers_[i]
                this.removeLayer(layer);
                this.addLayer(layer);
            }

            this.resetViewport();
            this.fitToScreen();

            this.fire("projection_change", { crs: this.options.crs, baseLayer: this.baseLayer_ });
        }

        // apply baselayer with correct projection
        if (this.baseLayer_) {
            const leafletLayer = this.baseLayer_.getMapLayer("leaflet");
            layer.enable("leaflet");
            layer.setProjection(this.options.crs);
            if (leafletLayer) this.leafletMap_.addLayer(leafletLayer);
        }
    }

    /**
     * Get the current map options
     * 
     * @returns  {object} options
     */
    getOptions() {
        const center = this.leafletMap_.getCenter()
        const zoom = this.leafletMap_.getZoom();
        return {
            options: Object.assign({}, this.options, {
                initialZoom: zoom,
                initialCenter: { lat: center.lat, lng: center.lng },
            })
        };
    }

    /**
     * Start drawing shapes on the map
     * 
     * Uses Leaflet.Draw version 0.4.12. Version 0.4.14 has a bug in the edit mode
     * https://github.com/Leaflet/Leaflet.draw/issues/804
     * 
     * @param {string} shape rectangle, polygon, line, point
     */
    startDrawing(shape) {
        this.stopDrawing();

        let options = { shapeOptions: { color: GeoJSONLayer.color } };
        let DrawingClass = null;

        switch (shape) {
            case "polygon":
                DrawingClass = L.Draw.Polygon;
                break;
            case "rectangle":
                DrawingClass = L.Draw.Rectangle;
                break;
            case "line":
                DrawingClass = L.Draw.Polyline;
                break;
            case "point":
                options = {
                    shapeOptions: {
                        radius: 6,
                        weight: 4
                    },
                    color: GeoJSONLayer.color
                };
                DrawingClass = L.Draw.CircleMarker;
                break;
            default:
                return;
        }

        this.shapeDrawer_ = new DrawingClass(this.leafletMap_, options);
        this.shapeDrawer_.enable();
    }

    /**
     * Abort drawing
     */
    stopDrawing() {
        if (this.shapeDrawer_) this.shapeDrawer_.disable();
        this.shapeDrawer_ = null;
    }

    /**
     * remove the map instance
     * 
     * @override
     */
    dispose() {
        this.leafletMap_.off();
        this.leafletMap_.remove();
        this.contextMenu_.dispose();
        super.dispose();
    }

}